import {ellipsis} from 'polished';
import React, {FC, useState} from 'react';

import {
  buildHoverParentClassName,
  directHoverSelector,
  hoverSelector,
} from '../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useUniqueElementId} from '../../../../libs/shared/helpers/src/react/hooks/uniqueIdHelpers';
import {FrontKeyboardEvent, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {rotate360Animation} from '../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {styled, UserTheme, useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {CalendarVersionAccessRolesEnum, CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {getHasRemoveAddedCalendars} from '../../../store/featureFlags/featureFlagsSelectors';
import {ViewingNowCalendar} from '../../../store/panels/calendars/calendarsPanelState';
import {CalendarsPanelId} from '../../../store/panels/panelStateBase';
import {CalendarsListItemDropdownContainer} from './calendarsListItemDropdownContainer';

/*
 * Types.
 */

type CalendarItem =
  | {calendarVersion: CalendarVersionModel; viewingNowCalendar?: undefined}
  | {viewingNowCalendar: ViewingNowCalendar; calendarVersion?: undefined};

interface CalendarsListItemProps {
  panelId: CalendarsPanelId;
  calendar: CalendarItem;
  calendarName: string;
  isImporting: boolean;
  isSelected: boolean;
  onClick: Handler;
  isMoreEnabled?: boolean;
  onRemoveCalendarClick?: HandlerOf<number>;
}

/*
 * Style.
 */

const StyledMoreButton = styled(ChromelessButton)`
  display: none;

  border-radius: 4px;
  background-color: ${(p) => p.theme.alphas.transparent};
  height: 22px;
  width: 22px;
  align-items: center;
  justify-content: center;

  ${hoverSelector} {
    display: flex;
  }

  ${directHoverSelector} {
    display: flex;
    background-color: ${(p) => p.theme.alphas.gray20};
  }
`;

const StyledCrownIconWrapperDiv = styled.div`
  margin-right: 2px;
  color: ${(p) => p.theme.greys.shade50};

  ${hoverSelector} {
    display: none;
  }
`;

interface StyledStatefulButtonProps {
  $isShowing: boolean;
}

const StyledRemoveIconButton = styled(IconButton)<StyledStatefulButtonProps>`
  display: none;
  padding: 0px;
  background: none;

  ${hoverSelector} {
    display: block;
  }
  ${(p) => p.$isShowing && 'display: block'}
`;

const StyledCalendarRowDiv = styled.div<StyledStatefulButtonProps>`
  margin: 0 10px;
  padding: 3px 0 3px 6px;

  display: grid;
  align-items: center;
  grid-template-columns: 16px 1fr auto auto;
  grid-column-gap: 8px;

  border-radius: 4px;

  color: ${(p) => p.theme.text};
  font-size: ${fontSizes.medium};

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
  ${(p) => p.$isShowing && `background-color: ${p.theme.alphas.gray10}`}
`;

const StyledRowButtonDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 3px;
  align-items: center;
`;

interface IconWrapperProps {
  $color: PaletteColorsEnum;
  $isSelected: boolean;
}
const StyledCheckBoxIconWrapperDiv = styled.div<IconWrapperProps>`
  margin: 3px 0;
  height: 16px;
  width: 16px;

  display: flex;
  justify-content: center;

  border-radius: 3px;
  background-color: ${(p) => (p.$isSelected ? p.theme.palette[p.$color].shade40 : 'none')};
  box-shadow: inset 0 0 0 1px
    ${(p) => (p.$isSelected ? p.theme.palette[p.$color].shade40 : p.theme.greys.shade60)};
`;

const StyledCalendarNameDiv = styled.div`
  ${ellipsis()};
  display: block;
`;

const StyledRepeatCircleIconDiv = styled.div`
  margin: 3px 0;
  color: ${(p) => p.theme.greys.shade50};
  ${rotate360Animation(2)}
`;

/*
 * Component.
 */

export const CalendarsListItem: FC<CalendarsListItemProps> = (props) => {
  const {calendar, calendarName, onClick, isMoreEnabled, onRemoveCalendarClick} = props;
  const {calendarVersion} = calendar;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const labelId = useUniqueElementId();
  const theme = useUserTheme();
  const hasRemoveAddedCalenders = useAppSelector(getHasRemoveAddedCalendars);

  const shouldRenderCrownIcon =
    calendarVersion &&
    !isDropdownOpen &&
    calendarVersion.isPrimary &&
    calendarVersion.accessRole === CalendarVersionAccessRolesEnum.OWNER;
  const shouldRenderRemoveButton =
    hasRemoveAddedCalenders &&
    calendarVersion &&
    !calendarVersion.isPrimary &&
    calendarVersion.accessRole !== CalendarVersionAccessRolesEnum.OWNER;

  return (
    <StyledCalendarRowDiv
      className={buildHoverParentClassName()}
      onClick={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        onClick();
      }}
      $isShowing={isDropdownOpen}
    >
      {renderCalendarRowIcon(props, labelId)}
      <TooltipCoordinator render={() => calendarName}>
        <StyledCalendarNameDiv id={labelId}>
          <TooltipOverflowCondition>{calendarName} </TooltipOverflowCondition>
        </StyledCalendarNameDiv>
      </TooltipCoordinator>
      {shouldRenderCrownIcon && renderCrownIcon()}
      <StyledRowButtonDiv>
        {isMoreEnabled && (
          <PopoverCoordinator
            renderButton={(isOpen) => renderButton(theme, isOpen)}
            renderModal={(modalProps) => renderDropdown(modalProps, props)}
            onPopoverToggle={(isOpen) => setIsDropdownOpen(isOpen)}
          />
        )}
        {shouldRenderRemoveButton && (
          <StyledRemoveIconButton
            iconName="clearSmall"
            label="Remove"
            color={theme.greys.shade70}
            hoverColor={theme.greys.shade80}
            onClick={(event) => {
              if (onRemoveCalendarClick && calendarVersion?.id) {
                event.preventDefault();
                return onRemoveCalendarClick(calendarVersion.id);
              }

              return null;
            }}
            $isShowing={isDropdownOpen && shouldRenderRemoveButton}
          />
        )}
      </StyledRowButtonDiv>
    </StyledCalendarRowDiv>
  );
};

function renderCalendarRowIcon(props: CalendarsListItemProps, labelId: string) {
  const {isImporting, isSelected} = props;

  // Define onKeyPress handler on the custom checkbox for accessibility.
  const onKeyPress = (event: FrontKeyboardEvent) => {
    if (event.key !== ' ') {
      return;
    }

    props.onClick();
  };

  if (isImporting && isSelected) {
    return (
      <StyledRepeatCircleIconDiv onKeyPress={onKeyPress}>
        <Icon name="repeatCircleSmall" />
      </StyledRepeatCircleIconDiv>
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledCheckBoxIconWrapperDiv
          $color={getCalendarColor(props)}
          $isSelected={isSelected}
          role="checkbox"
          aria-checked={isSelected}
          aria-labelledby={labelId}
          tabIndex={0}
          onKeyPress={onKeyPress}
        >
          {isSelected && <Icon name="checkmarkBoxSmall" color={theme.greys.white} size={16} />}
        </StyledCheckBoxIconWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

function getCalendarColor(props: CalendarsListItemProps) {
  if (props.calendar.calendarVersion) {
    return props.calendar.calendarVersion.color;
  }

  return props.calendar.viewingNowCalendar.color;
}

function renderCrownIcon() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledCrownIconWrapperDiv>
          <Icon name="crownSmall" color={theme.greys.shade70} />
        </StyledCrownIconWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderButton(theme: UserTheme, isOpen: boolean) {
  return (
    <TooltipCoordinator render={() => <Trans id="calendar-list-item-show-actions">Show actions</Trans>}>
      <StyledMoreButton hasHover={isOpen}>
        <Icon
          name="moreVertical"
          color={theme.greys.shade70}
          hoverColor={theme.greys.shade80}
          hasHover={isOpen}
        />
      </StyledMoreButton>
    </TooltipCoordinator>
  );
}

function renderDropdown(popoverProps: PopoverTransferProps, {panelId, calendar}: CalendarsListItemProps) {
  return (
    calendar.calendarVersion && (
      <CalendarsListItemDropdownContainer
        {...popoverProps}
        panelId={panelId}
        calendarVersion={calendar.calendarVersion}
      />
    )
  );
}
